import { useState } from 'react';
import { MessageSquare, Users, Clock, ThumbsUp, ThumbsDown, AlertTriangle, TrendingUp, TrendingDown, Minus, Download, Info, Wrench } from 'lucide-react';

const TOOL_CALLS = [
  { name: '当前告警批量查询', count: 3421, avgLatency: 0.8 },
  { name: 'KPI指标时序查询', count: 2143, avgLatency: 1.2 },
  { name: '告警详情查询', count: 1876, avgLatency: 0.6 },
  { name: 'alarm_query_tool (MCP)', count: 1532, avgLatency: 0.9 },
  { name: '设备配置快照查询', count: 987, avgLatency: 1.5 },
];

// 满意度因"非每条回复都有反馈"样本稀疏而无意义，改为：绝对反馈量(👍/👎) + 差评率(仅在有反馈样本中计算)
const AGENT_STATS = [
  { name: '告警智能诊断', source: 'API接入', sessions: 1284, successRate: 98.2, avgLatency: 1.4, likes: 86, dislikes: 5, trend: 'up', errorCount: 23 },
  { name: 'KPI异常分析', source: 'API接入', sessions: 876, successRate: 96.5, avgLatency: 2.1, likes: 52, dislikes: 9, trend: 'up', errorCount: 31 },
  { name: '设备配置核查', source: 'API接入', sessions: 654, successRate: 99.1, avgLatency: 0.9, likes: 41, dislikes: 2, trend: 'stable', errorCount: 6 },
  { name: '工单处理助手', source: 'API接入', sessions: 432, successRate: 94.3, avgLatency: 3.2, likes: 18, dislikes: 11, trend: 'down', errorCount: 25 },
  { name: '网络拓扑查询', source: '知+接入', sessions: 310, successRate: 91.8, avgLatency: 4.5, likes: 9, dislikes: 14, trend: 'down', errorCount: 25 },
];

const HOURLY = [8,12,18,24,31,28,22,19,15,18,24,29,34,38,32,27,22,18,14,10,8,6,5,4];

function StatCard({ icon: Icon, label, value, sub, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

function MiniBarChart({ data }) {
  const max = Math.max(...data);
  return (
    <div className="flex items-end gap-0.5 h-12">
      {data.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm bg-blue-400 opacity-70 hover:opacity-100 transition-opacity"
          style={{ height: `${(v / max) * 100}%` }}
          title={`${v} 次`}
        />
      ))}
    </div>
  );
}

function TrendIcon({ trend }) {
  if (trend === 'up') return <TrendingUp className="w-3.5 h-3.5 text-green-500" />;
  if (trend === 'down') return <TrendingDown className="w-3.5 h-3.5 text-red-400" />;
  return <Minus className="w-3.5 h-3.5 text-gray-400" />;
}

// 反馈展示：绝对点赞/点踩数 + 差评率（仅在有反馈样本中计算，反馈量过低时标注"样本不足"）
function FeedbackCell({ likes, dislikes }) {
  const total = likes + dislikes;
  const badRate = total > 0 ? Math.round((dislikes / total) * 100) : null;
  const lowSample = total < 15;
  return (
    <div className="flex items-center gap-2.5 text-xs">
      <span className="flex items-center gap-0.5 text-green-600"><ThumbsUp className="w-3 h-3" />{likes}</span>
      <span className="flex items-center gap-0.5 text-red-400"><ThumbsDown className="w-3 h-3" />{dislikes}</span>
      {badRate !== null && (
        <span className={`px-1.5 py-0.5 rounded ${lowSample ? 'bg-gray-100 text-gray-400' : badRate >= 30 ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'}`}
          title={lowSample ? '反馈样本不足，差评率仅供参考' : '差评率 = 点踩 / 总反馈'}>
          差评 {badRate}%{lowSample ? ' ⚠' : ''}
        </span>
      )}
    </div>
  );
}

function TokenNotice() {
  return (
    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
      <span>
        <strong>Token消耗说明：</strong>平台所有Agent均为API外部接入型，大模型调用在项目组服务侧发生，平台不可见Agent内部Token消耗。如需统计，请项目组通过OpenTelemetry Trace主动上报。
      </span>
    </div>
  );
}

export default function OperationsDashboard() {
  const [timeRange, setTimeRange] = useState('7d');
  const ranges = [{ key: '1d', label: '今日' }, { key: '7d', label: '近7天' }, { key: '30d', label: '近30天' }];

  const totalSessions = AGENT_STATS.reduce((s, a) => s + a.sessions, 0);
  const avgSuccess = (AGENT_STATS.reduce((s, a) => s + a.successRate, 0) / AGENT_STATS.length).toFixed(1);
  const avgLatency = (AGENT_STATS.reduce((s, a) => s + a.avgLatency, 0) / AGENT_STATS.length).toFixed(1);
  const totalLikes = AGENT_STATS.reduce((s, a) => s + a.likes, 0);
  const totalDislikes = AGENT_STATS.reduce((s, a) => s + a.dislikes, 0);
  const totalFeedback = totalLikes + totalDislikes;
  const feedbackRate = ((totalFeedback / totalSessions) * 100).toFixed(1);

  return (
    <div className="max-w-5xl mx-auto px-6 py-6">
      {/* 页头 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">运营数据看板</h2>
          <p className="text-sm text-gray-500 mt-0.5">平台可观测指标 · 不含Agent内部Token消耗</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            {ranges.map(r => (
              <button
                key={r.key}
                onClick={() => setTimeRange(r.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  timeRange === r.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Download className="w-4 h-4" /> 月度报告
          </button>
        </div>
      </div>

      {/* 概览卡片 */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard icon={MessageSquare} label="总会话数" value={totalSessions.toLocaleString()} sub="较上周 +12%" color="blue" />
        <StatCard icon={Users} label="活跃用户" value="342" sub="日均独立用户" color="purple" />
        <StatCard icon={Clock} label="平均响应时延" value={`${avgLatency}s`} sub="P95: 4.8s" color="orange" />
        <StatCard icon={ThumbsUp} label="累计反馈量" value={`${totalLikes}/${totalDislikes}`} sub={`反馈率 ${feedbackRate}% · 👍赞/👎踩`} color="green" />
      </div>

      {/* 指标口径说明 */}
      <div className="flex items-start gap-2 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span><strong>反馈口径说明：</strong>因并非每条回复用户都会反馈，故不展示"满意度百分比"（小样本下失真）。改用<strong>绝对反馈量</strong>（点赞/点踩数）+ <strong>差评率</strong>（仅在有反馈样本中计算，样本不足时标注 ⚠ 仅供参考）。</span>
      </div>

      {/* Token说明提示 */}
      <TokenNotice />

      {/* 会话趋势图 */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-800">会话量趋势（24h）</h3>
          <span className="text-xs text-gray-400">每格代表1小时</span>
        </div>
        <MiniBarChart data={HOURLY} />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
        </div>
      </div>

      {/* 工具调用量（真实数据源 = 能力注册中心网关） */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-gray-800">工具调用量 Top5</h3>
          </div>
          <span className="text-xs text-gray-400">数据源：能力注册中心网关（含超级智能体调用）</span>
        </div>
        <div className="space-y-2.5">
          {TOOL_CALLS.map((tool, i) => {
            const max = TOOL_CALLS[0].count;
            return (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-4">{i + 1}</span>
                <span className="text-sm text-gray-700 w-48 truncate">{tool.name}</span>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(tool.count / max) * 100}%` }} />
                </div>
                <span className="text-sm font-medium text-gray-700 w-16 text-right">{tool.count.toLocaleString()}</span>
                <span className="text-xs text-gray-400 w-14 text-right">{tool.avgLatency}s</span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-100">
          说明：仅统计经平台网关的调用（API代理 + MCP + 超级智能体）。项目组绕过平台的直连调用不在此列。
        </p>
      </div>

      {/* Agent效能排行 */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800">Agent 效能排行</h3>
          <span className="text-xs text-gray-400">按会话量降序</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500">
                <th className="text-left px-5 py-3 font-medium">Agent名称</th>
                <th className="text-left px-4 py-3 font-medium">来源</th>
                <th className="text-right px-4 py-3 font-medium">会话量</th>
                <th className="text-right px-4 py-3 font-medium">调用成功率</th>
                <th className="text-right px-4 py-3 font-medium">平均时延</th>
                <th className="px-4 py-3 font-medium w-44">反馈（👍/👎 · 差评率）</th>
                <th className="text-center px-4 py-3 font-medium">趋势</th>
                <th className="text-right px-5 py-3 font-medium">异常次数</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {AGENT_STATS.map((agent, i) => (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 text-xs font-bold text-gray-400 flex items-center justify-center">{i + 1}</span>
                      <span className="font-medium text-gray-800">{agent.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{agent.source}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-700">{agent.sessions.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${agent.successRate >= 97 ? 'text-green-600' : agent.successRate >= 94 ? 'text-yellow-600' : 'text-red-500'}`}>
                      {agent.successRate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">{agent.avgLatency}s</td>
                  <td className="px-4 py-3 w-44"><FeedbackCell likes={agent.likes} dislikes={agent.dislikes} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center"><TrendIcon trend={agent.trend} /></div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className={`text-xs font-medium ${agent.errorCount > 20 ? 'text-red-500' : agent.errorCount > 10 ? 'text-yellow-600' : 'text-gray-500'}`}>
                      {agent.errorCount > 0 && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                      {agent.errorCount}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-400">
          调用成功率 = HTTP 2xx 响应次数 / 总调用次数 · 反馈为绝对量(👍/👎) + 差评率(有反馈样本中计) · Token消耗不可统计（见上方说明）
        </div>
      </div>
    </div>
  );
}
